'use client';

import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import ShimmerContainer from '@alltrails/core/components/ShimmerContainer';
import useIsVisible from '@alltrails/core/hooks/useIsVisible';
import useMediaQuery from '@alltrails/core/hooks/useMediaQuery';
import { useExposureEvent } from '@alltrails/experiments';
import { useAppSelector } from '@/utils/redux';
import styles from './LocationMap.module.scss';
import { MapProps } from './LocationMapWrapper';

const mapLoadingState = (
  <div className={styles.mapContainer}>
    <ShimmerContainer className={styles.shimmerContainer}>
      <div />
    </ShimmerContainer>
  </div>
);

const LocationMapWrapper = dynamic(() => import('./LocationMapWrapper'), {
  ssr: false,
  loading: () => mapLoadingState
});

type DynamicLocationMapProps = MapProps & {
  mobileDeferLoad?: boolean;
};

const DynamicLocationMap = (props: DynamicLocationMapProps) => {
  const { mobileDeferLoad, ...mapProps } = props;
  const mapWrapperRef = useRef<HTMLDivElement>(null);
  const isInView = useIsVisible(mapWrapperRef, { rootMargin: '100px' });
  const [isDeferredMapLoaded, setIsDeferredMapLoaded] = useState(false);
  const isMobileBrowser = useAppSelector((state: { context: { mobileBrowser: boolean } }) => state.context?.mobileBrowser);
  const isTabletOrMobile = useMediaQuery(`(max-width: 1120px)`, isMobileBrowser);
  const deferLoadIntoView = isTabletOrMobile && mobileDeferLoad;

  const movedMapOnMobileExposureEvent = useExposureEvent('web-growth-rm-move-location-mobile-map');

  useEffect(() => {
    if (isInView && deferLoadIntoView) {
      setIsDeferredMapLoaded(true);
    }
  }, [isInView]);

  useEffect(() => {
    if (isTabletOrMobile) {
      movedMapOnMobileExposureEvent();
    }
  }, [isTabletOrMobile, movedMapOnMobileExposureEvent]);

  const showMap = !deferLoadIntoView || isDeferredMapLoaded;

  if (!showMap) {
    return <div ref={mapWrapperRef}>{mapLoadingState}</div>;
  }

  return <LocationMapWrapper {...mapProps} />;
};

export default DynamicLocationMap;
